import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getReferral, refer } from '../services/growthApi';

/*
 * ReferralCard — the user's referral hub. Shows their shareable link with a
 * copy-to-clipboard button, an invite-by-email form, and a running count of
 * invites sent. Blue brand, hairline card.
 */
function ReferralCard() {
  const [referral, setReferral] = useState(null); // { code, referralUrl, count }
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getReferral();
      setReferral(res.data);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not load your referral link');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const fullUrl = referral
    ? `${window.location.origin}${referral.referralUrl}`
    : '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error('Could not copy link');
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value) return;
    setInviting(true);
    try {
      await refer(value);
      toast.success(`Invite sent to ${value}`);
      setEmail('');
      setReferral((r) => (r ? { ...r, count: (r.count || 0) + 1 } : r));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not send invite');
    } finally {
      setInviting(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-500">
        Loading your referral link…
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-900">Your referral link</h3>
        <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
          {referral?.count || 0} invited
        </span>
      </div>

      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <input
          type="text"
          readOnly
          value={fullUrl}
          onFocus={(e) => e.target.select()}
          className="w-full flex-1 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:outline-none"
        />
        <button
          type="button"
          onClick={handleCopy}
          className="rounded-lg bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
        >
          {copied ? 'Copied!' : 'Copy link'}
        </button>
      </div>

      <form onSubmit={handleInvite} className="mt-5">
        <label htmlFor="invite-email" className="block text-sm font-medium text-gray-700">
          Invite by email
        </label>
        <div className="mt-2 flex flex-col gap-2 sm:flex-row">
          <input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="friend@example.com"
            className="w-full flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-[#2563EB] focus:outline-none focus:ring-1 focus:ring-[#2563EB]"
          />
          <button
            type="submit"
            disabled={inviting || !email.trim()}
            className="rounded-lg bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {inviting ? 'Sending…' : 'Send invite'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ReferralCard;
